// Types, requests and React Query hooks for the site page's Keys tab (Fobs list, Audit log and
// fob detail). Enums arrive as strings and properties as camelCase, as elsewhere in the API.
import axios from "axios";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { BASE_API_URL } from "store/Me";

/** Mirrors ConnectApi's PhysicalKeyExternalSyncStatus. */
export type PhysicalKeyExternalSyncStatus =
  | "Synced"
  | "Unassigned"
  | "Removed"
  | "Skipped"
  | "Failed";

/** A fob's state on one destination site (e.g. a sister Brivo site). */
export interface PhysicalKeyExternalSync {
  destinationSiteId: string;
  destinationSiteName: string | null;
  /** The destination's SiteSystemType name, e.g. "Brivo". */
  externalSystemType: string;
  status: PhysicalKeyExternalSyncStatus;
  statusReason: string | null;
  lastSyncedTimestamp: string | null;
  lastAttemptTimestamp: string;
  externalCredentialId: string | null;
  externalCredentialFormat: string | null;
  externalUserId: string | null;
  createdByLivly: boolean;
}

export interface SiteKeyResponse {
  /** The number printed on the fob (the fob #). */
  badgeId: string;
  /** The badge id Schlage locks use; differs from badgeId only for BUC3_37Bit fobs. */
  schlageBadgeId?: string | null;
  credential: string;
  format: string;
  guestId: string | null;
  physicalKeyGroupId: string | null;
  encodedTimestamp: string | null;
  validFrom: string | null;
  validTo: string | null;
  siteId: string;
  /** Only on the site key list and single-key endpoints; null or absent elsewhere (or on an older API). */
  externalSyncs?: PhysicalKeyExternalSync[] | null;
}

/** Every key query for a site starts with this, so one invalidation refreshes them all. */
export const siteKeysQueryKey = (siteId: string) => ["sites", "keys", siteId] as const;

async function getSiteKeys(
  siteId: string,
  search: string,
  skip: number,
  take: number,
): Promise<SiteKeyResponse[]> {
  const params = new URLSearchParams();
  if (search) params.append("searchString", search);
  // The endpoint pages with skip/take (page/pageSize are ignored and return every key).
  params.append("skip", skip.toString());
  params.append("take", take.toString());
  const result = await axios.get<SiteKeyResponse[]>(
    `${BASE_API_URL}/api/Sites/${siteId}/physicalKeys?${params.toString()}`,
  );
  return result.data ?? [];
}

export function useSiteKeys(
  siteId: string,
  search: string,
  skip: number,
  take: number,
) {
  return useQuery({
    queryKey: [...siteKeysQueryKey(siteId), "list", { search, skip, take }],
    queryFn: () => getSiteKeys(siteId, search, skip, take),
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------------------------
// Audit (site-wide and per key)
// ---------------------------------------------------------------------------------------------

export interface PhysicalKeyAuditNamed {
  /** A guest id, key group id or (for related-site events) the sister site's id. */
  id: string | null;
  /** The resolved name; empty when it couldn't be looked up. */
  name: string | null;
}

export interface PhysicalKeyAuditEncoding {
  addAccess: { name: string; noTourId: number; validFrom: string; validTo: string }[];
  removeAccess: { name: string; noTourId: number }[];
}

export interface PhysicalKeyAuditRecord {
  id: string;
  timestamp: string;
  credential: string;
  /** The fob # (the number printed on the fob). */
  badgeId: string | null;
  /** A PhysicalKeyEvent name. */
  event: string;
  actor: { auth0UserId: string | null; name: string | null; avatarUrl?: string | null } | null;
  oldAssignment: PhysicalKeyAuditNamed | null;
  newAssignment: PhysicalKeyAuditNamed | null;
  encoding: PhysicalKeyAuditEncoding | null;
}

export interface PaginatedResponse<T> {
  totalCount: number;
  data: T[];
}

export interface SiteKeyAuditFilters {
  /** Only this fob's audit (the per-key endpoint); the whole site's when omitted. */
  credential?: string;
  /** A PhysicalKeyEvent name, or "" for every event. */
  event: string;
  search: string;
  page: number;
  pageSize: number;
}

async function getSiteKeyAudit(
  siteId: string,
  { credential, event, search, page, pageSize }: SiteKeyAuditFilters,
): Promise<PaginatedResponse<PhysicalKeyAuditRecord>> {
  const params = new URLSearchParams();
  if (event) params.append("event", event);
  if (search) params.append("searchString", search);
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  const path = credential
    ? `physicalKeys/${encodeURIComponent(credential)}/audit`
    : "physicalKeys/audit";
  const result = await axios.get<PaginatedResponse<PhysicalKeyAuditRecord>>(
    `${BASE_API_URL}/api/Sites/${siteId}/${path}?${params.toString()}`,
  );
  return result.data;
}

export function useSiteKeyAudit(siteId: string, filters: SiteKeyAuditFilters) {
  return useQuery({
    queryKey: [...siteKeysQueryKey(siteId), "audit", filters],
    queryFn: () => getSiteKeyAudit(siteId, filters),
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------------------------
// One fob: detail, lock activity and the sister-site retry
// ---------------------------------------------------------------------------------------------

async function getSiteKey(siteId: string, credential: string): Promise<SiteKeyResponse> {
  const result = await axios.get<SiteKeyResponse>(
    `${BASE_API_URL}/api/Sites/${siteId}/physicalKeys/${encodeURIComponent(credential)}`,
  );
  return result.data;
}

export function useSiteKey(siteId: string, credential: string) {
  return useQuery({
    queryKey: [...siteKeysQueryKey(siteId), "detail", credential],
    queryFn: () => getSiteKey(siteId, credential),
  });
}

export interface PhysicalKeyLockActivity {
  timestamp: string;
  lockId: string;
  lockName: string | null;
  /** A LockActivityCategory name. */
  lockActivityCategory: string;
  badgeId: string | null;
  credential: string;
  details: string | null;
}

async function getSiteKeyActivity(
  siteId: string,
  credential: string,
  page: number,
  pageSize: number,
): Promise<PaginatedResponse<PhysicalKeyLockActivity>> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("pageSize", pageSize.toString());
  const result = await axios.get<PaginatedResponse<PhysicalKeyLockActivity>>(
    `${BASE_API_URL}/api/Sites/${siteId}/physicalKeys/${encodeURIComponent(credential)}/lock-activity?${params.toString()}`,
  );
  return result.data;
}

export function useSiteKeyActivity(
  siteId: string,
  credential: string,
  page: number,
  pageSize: number,
) {
  return useQuery({
    queryKey: [...siteKeysQueryKey(siteId), "activity", credential, { page, pageSize }],
    queryFn: () => getSiteKeyActivity(siteId, credential, page, pageSize),
    placeholderData: keepPreviousData,
  });
}

/**
 * Re-runs the fob's sync to every sister site (PUT .../sister-sync, which runs inline). It fails
 * if any sister site fails, but the sync records are updated either way, so every key query for
 * the site is refreshed when it settles.
 */
export function useRetrySisterSync(siteId: string, credential: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      axios.put(
        `${BASE_API_URL}/api/Sites/${siteId}/physicalKeys/${encodeURIComponent(credential)}/sister-sync`,
      ),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: siteKeysQueryKey(siteId) }),
  });
}

/** A readable message from a failed request. */
export function requestErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    message?: string;
    response?: { data?: unknown };
  } | null;
  const data = err?.response?.data as { message?: unknown } | string | undefined;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object" && typeof data.message === "string" && data.message) {
    return data.message;
  }
  return err?.message || fallback;
}
